/**
 * Shared TypeScript types for terminal components.
 *
 * @module components/terminal/types
 */

import type { ConnectionState } from '@/hooks';

/** Maximum number of terminal tabs allowed */
export const MAX_TERMINAL_TABS = 10;

/** Split panel info for a tab */
export interface SplitPanel {
  /** Unique identifier for the split panel */
  id: string;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Session ID from the server (if connected) */
  sessionId: string | null;
}

/** Terminal tab representation */
export interface TerminalTab {
  /** Unique identifier for the tab */
  id: string;
  /** Display name (e.g., "bash (1)") */
  name: string;
  /** Shell name (e.g., "bash") */
  shellName: string;
  /** Instance number for this shell type */
  instanceNumber: number;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Session ID from the server (if connected) */
  sessionId: string | null;
  /** Optional split panel - when present, tab shows two terminals side by side */
  split: SplitPanel | null;
}

/** Panel type within a tab */
export type PanelType = 'main' | 'split';

/** Props for the main terminal container */
export interface TerminalContainerProps {
  /** Sandbox ID to connect terminals to */
  sandboxId: string;
  /** Optional container name for pod sandboxes (appended to exec/stream URL) */
  containerName?: string;
  /** Optional sandbox group ID — when set, uses group-scoped URL paths */
  sandboxGroupId?: string;
  /** Callback when the terminal container is closed */
  onClose?: () => void;
  /** Callback when a WebSocket connection error occurs (e.g., sandbox stopped) */
  onConnectionError?: () => void;
}

/** Props for individual terminal panel */
export interface TerminalPanelProps {
  /** Sandbox ID to connect to */
  sandboxId: string;
  /** Optional container name for pod sandboxes */
  containerName?: string;
  /** Optional sandbox group ID — when set, uses group-scoped URL paths */
  sandboxGroupId?: string;
  /** Tab ID for this panel */
  tabId: string;
  /** Whether this is the main panel or the split panel */
  panelType: PanelType;
  /** Whether this panel is currently active/visible */
  isActive: boolean;
  /** Callback when a WebSocket connection error occurs */
  onConnectionError?: () => void;
}

/** Props for the terminal tab bar */
export interface TerminalTabBarProps {
  /** Current tabs */
  tabs: TerminalTab[];
  /** Currently active tab ID */
  activeTabId: string | null;
  /** Whether in fullscreen mode */
  isFullscreen: boolean;
  /** Whether the active tab has a split panel */
  activeTabHasSplit: boolean;
  /** Callback to add a new tab */
  onAddTab: () => void;
  /** Callback to close a tab */
  onCloseTab: (tabId: string) => void;
  /** Callback to select a tab */
  onSelectTab: (tabId: string) => void;
  /** Callback to toggle split view for active tab */
  onToggleSplit: () => void;
  /** Callback to toggle fullscreen */
  onToggleFullscreen: () => void;
  /** Callback to close the entire terminal container */
  onClose?: () => void;
  /** Callback to copy terminal content */
  onCopy?: () => void;
  /** Current copy state: 'idle' | 'copied' */
  copyState?: 'idle' | 'copied';
}
